import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import {
  FiHash, FiX, FiMessageSquare, FiTrendingUp,
  FiRefreshCw, FiStar, FiBarChart2, FiCheck,
  FiSearch, FiPhone, FiFileText, FiLock, FiZap,
  FiAlertTriangle, FiPlus, FiChevronLeft, FiGrid,
  FiMapPin, FiCopy,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useAmps } from '../../context/AmpContext';
import * as ampService from '../../services/ampService';
import { listExternalMcp, dedupeExternalMcps, listInstalledSkills } from '../../services/marketplaceService';
import type { ExternalMcpItem, InstalledSkillItem } from '../../services/marketplaceService';
import type { Channel } from './chat.types';
import '../../styles/AddChannelModal.css';
import '../../styles/AmpsGridPage.css';

interface AddChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (channel: Omit<Channel, 'id' | 'guid' | 'createdAt'>) => void;
  onSave?: (channelId: string, updates: Omit<Channel, 'id' | 'guid' | 'createdAt'>) => void;
  channelToEdit?: Channel;
  inline?: boolean;
}

const ICON_OPTIONS = [
  { id: 'hash', icon: <FiHash size={18} />, label: 'General' },
  { id: 'message', icon: <FiMessageSquare size={18} />, label: 'Messages' },
  { id: 'trending', icon: <FiTrendingUp size={18} />, label: 'Analytics' },
  { id: 'refresh', icon: <FiRefreshCw size={18} />, label: 'Renewals' },
  { id: 'star', icon: <FiStar size={18} />, label: 'Favorites' },
  { id: 'chart', icon: <FiBarChart2 size={18} />, label: 'Reports' },
];

const COLOR_OPTIONS = [
  '#8B5CF6', '#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6',
  '#EF4444', '#14B8A6', '#F97316', '#22D3EE',
];

const AMP_ICON_MAP: Record<string, React.ReactNode> = {
  message: <FiMessageSquare size={16} />,
  search: <FiSearch size={16} />,
  phone: <FiPhone size={16} />,
  file: <FiFileText size={16} />,
  image: <RiSparklingFill size={16} />,
  scan: <FiGrid size={16} />,
  dollar: <FiTrendingUp size={16} />,
  chart: <FiBarChart2 size={16} />,
  shield: <FiLock size={16} />,
  database: <FiHash size={16} />,
  car: <FiMapPin size={16} />,
  compare: <FiCopy size={16} />,
  zap: <FiZap size={16} />,
};

// Icon map sized for the create skill form (matches AmpsGridPage)
const SKILL_ICON_MAP: Record<string, React.ReactNode> = {
  message: <FiMessageSquare size={18} />,
  search: <FiSearch size={18} />,
  phone: <FiPhone size={18} />,
  file: <FiFileText size={18} />,
  image: <RiSparklingFill size={18} />,
  scan: <FiGrid size={18} />,
  dollar: <FiTrendingUp size={18} />,
  chart: <FiBarChart2 size={18} />,
  shield: <FiLock size={18} />,
  database: <FiHash size={18} />,
  car: <FiMapPin size={18} />,
  compare: <FiCopy size={18} />,
  zap: <FiZap size={18} />,
};

const AVAILABLE_SKILL_ICONS = [
  'zap', 'message', 'search', 'phone', 'file', 'scan',
  'dollar', 'chart', 'shield', 'database', 'car', 'compare', 'image',
];

const AddChannelModal: React.FC<AddChannelModalProps> = ({ isOpen, onClose, onAdd, onSave, channelToEdit, inline = false }) => {
  const isEditing = Boolean(channelToEdit);
  const { premadeAmps, customAmps, initialLoading: ampsLoading, error: ampsError, fetchPremadeAmps, fetchCustomAmps, createAmp } = useAmps();
  const [name, setName] = useState(channelToEdit?.name ?? '');
  const [description, setDescription] = useState(channelToEdit?.description ?? '');
  const [selectedIcon, setSelectedIcon] = useState(channelToEdit?.icon ?? 'hash');
  const [selectedColor, setSelectedColor] = useState(channelToEdit?.color ?? COLOR_OPTIONS[0]);
  const [isWorkspace, setIsWorkspace] = useState(true);
  const [selectedAmps, setSelectedAmps] = useState<string[]>(channelToEdit?.ampIds ?? []);
  const [ampTab, setAmpTab] = useState<'premade' | 'yours'>('premade');
  const [ampSearch, setAmpSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autoSelectedRef = React.useRef(false);

  // Re-seed form when opening or when the channel being edited changes
  React.useEffect(() => {
    if (!isOpen) { autoSelectedRef.current = false; return; }
    if (channelToEdit) {
      setName(channelToEdit.name);
      setDescription(channelToEdit.description ?? '');
      setSelectedIcon(channelToEdit.icon ?? 'hash');
      setSelectedColor(channelToEdit.color ?? COLOR_OPTIONS[0]);
      setSelectedAmps(channelToEdit.ampIds ?? []);
      setSelectedMcps((channelToEdit.mcpIds ?? []).map(m => m.mcpId));
      setSelectedMarketplaceSkills((channelToEdit.skillIds ?? []).map(s => s.skillId));
      setError(null);
    } else {
      setName('');
      setDescription('');
      setSelectedIcon('hash');
      setSelectedColor(COLOR_OPTIONS[0]);
      setSelectedAmps(premadeAmps.length > 0 ? premadeAmps.map(a => a.id) : []);
      autoSelectedRef.current = premadeAmps.length > 0;
      setSelectedMcps([]);
      setSelectedMarketplaceSkills([]);
      setAmpSearch('');
      setSelectedCategory('All');
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, channelToEdit?.id]);

  // If premadeAmps load after the modal opens (async), auto-select all for new workspace
  React.useEffect(() => {
    if (!isOpen || channelToEdit || premadeAmps.length === 0 || autoSelectedRef.current) return;
    setSelectedAmps(premadeAmps.map(a => a.id));
    autoSelectedRef.current = true;
  }, [isOpen, channelToEdit, premadeAmps]);

  // Create Skill inline form state
  const [showCreateSkill, setShowCreateSkill] = useState(false);
  const [skillName, setSkillName] = useState('');
  const [skillCategory, setSkillCategory] = useState('');
  const [skillDescription, setSkillDescription] = useState('');
  const [skillInstructions, setSkillInstructions] = useState('');
  const [skillIcon, setSkillIcon] = useState('zap');
  const [skillKnowledgeBaseFile, setSkillKnowledgeBaseFile] = useState<File | null>(null);
  const [skillDragActive, setSkillDragActive] = useState(false);
  const [skillShareWithCompany, setSkillShareWithCompany] = useState(true);
  const [skillGeneratingPrompt, setSkillGeneratingPrompt] = useState(false);
  const [isCreatingSkill, setIsCreatingSkill] = useState(false);
  const [skillError, setSkillError] = useState<string | null>(null);

  // MCP server state
  const [mcpList, setMcpList] = useState<ExternalMcpItem[]>([]);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [selectedMcps, setSelectedMcps] = useState<string[]>([]);
  const [mcpSearch, setMcpSearch] = useState('');

  // Marketplace-installed Skills — merged into the "Your Skills" tab alongside custom Skills
  const [marketplaceSkills, setMarketplaceSkills] = useState<InstalledSkillItem[]>([]);
  const [marketplaceSkillsLoading, setMarketplaceSkillsLoading] = useState(false);
  const [selectedMarketplaceSkills, setSelectedMarketplaceSkills] = useState<string[]>([]);

  const skillCategoryOptions = useMemo(
    () => Array.from(new Set(premadeAmps.map(a => a.category).filter(Boolean))) as string[],
    [premadeAmps]
  );


  // Refresh AMPs when modal opens to ensure they're loaded
  React.useEffect(() => {
    if (isOpen && premadeAmps.length === 0) {
      fetchPremadeAmps();
    }
    if (isOpen && customAmps.length === 0) {
      fetchCustomAmps();
    }
  }, [isOpen, premadeAmps.length, customAmps.length, fetchPremadeAmps, fetchCustomAmps]);

  // Fetch MCP servers when modal opens
  React.useEffect(() => {
    if (!isOpen) return;
    setMcpLoading(true);
    listExternalMcp()
      .then(data => setMcpList(dedupeExternalMcps(data)))
      .catch(() => {})
      .finally(() => setMcpLoading(false));
  }, [isOpen]);

  // Fetch installed Marketplace Skills when modal opens
  React.useEffect(() => {
    if (!isOpen) return;
    setMarketplaceSkillsLoading(true);
    listInstalledSkills()
      .then(setMarketplaceSkills)
      .catch(() => {})
      .finally(() => setMarketplaceSkillsLoading(false));
  }, [isOpen]);

  const premadeCategories = useMemo(() => {
    const cats = Array.from(new Set(premadeAmps.map(a => a.category).filter(Boolean)));
    return ['All', ...cats];
  }, [premadeAmps]);

  // "Your Skills" combines workspace-created custom Skills with Skills installed from the
  // Marketplace — both are just Skills to the user, so they belong in one list (AI-83).
  const combinedYourSkills = useMemo(() => [
    ...customAmps.map(a => ({ ...a, __source: 'amp' as const })),
    ...marketplaceSkills.map(sk => ({
      id: sk.id, name: sk.name, description: sk.category || 'Marketplace skill',
      category: sk.category, icon: sk.icon, color: '#8B5CF6', __source: 'marketplace' as const,
    })),
  ], [customAmps, marketplaceSkills]);

  const filteredAmps = useMemo(() => {
    const sourceAmps = ampTab === 'premade'
      ? premadeAmps.map(a => ({ ...a, __source: 'amp' as const }))
      : combinedYourSkills;
    let result = sourceAmps;
    if (ampTab === 'premade' && selectedCategory !== 'All') {
      result = result.filter(amp => amp.category === selectedCategory);
    }
    if (!ampSearch.trim()) return result;
    const search = ampSearch.toLowerCase();
    return result.filter(
      amp => amp.name.toLowerCase().includes(search) ||
        amp.description.toLowerCase().includes(search) ||
        amp.category.toLowerCase().includes(search)
    );
  }, [ampTab, premadeAmps, combinedYourSkills, ampSearch, selectedCategory]);

  const toggleAmp = useCallback((ampId: string) => {
    setSelectedAmps(prev =>
      prev.includes(ampId)
        ? prev.filter(id => id !== ampId)
        : [...prev, ampId]
    );
    setError(null);
  }, []);

  const filteredMcps = useMemo(() => {
    if (!mcpSearch.trim()) return mcpList;
    const s = mcpSearch.toLowerCase();
    return mcpList.filter(m => m.name.toLowerCase().includes(s) || m.url.toLowerCase().includes(s));
  }, [mcpList, mcpSearch]);

  const toggleMcp = useCallback((id: string) => {
    setSelectedMcps(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const toggleMarketplaceSkill = useCallback((id: string) => {
    setSelectedMarketplaceSkills(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const resetSkillForm = useCallback(() => {
    setSkillName('');
    setSkillCategory('');
    setSkillDescription('');
    setSkillInstructions('');
    setSkillIcon('zap');
    setSkillKnowledgeBaseFile(null);
    setSkillDragActive(false);
    setSkillShareWithCompany(true);
    setSkillGeneratingPrompt(false);
    setSkillError(null);
  }, []);

  const handleSkillDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setSkillDragActive(true);
    else if (e.type === 'dragleave') setSkillDragActive(false);
  }, []);

  const handleSkillDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSkillDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type.startsWith('text/') || file.type === 'application/pdf' || /\.(txt|pdf|doc|docx)$/i.test(file.name))) {
      setSkillKnowledgeBaseFile(file);
    }
  }, []);

  const handleSkillFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setSkillKnowledgeBaseFile(e.target.files[0]);
  }, []);

  const handleGenerateSkillPrompt = useCallback(async () => {
    if (skillGeneratingPrompt) return;
    setSkillGeneratingPrompt(true);
    try {
      const generated = await ampService.generatePrompt(skillName.trim() || 'Create a system prompt for an AI assistant');
      setSkillInstructions(generated);
    } catch (err) {
      console.error('Failed to generate prompt:', err);
    } finally {
      setSkillGeneratingPrompt(false);
    }
  }, [skillName, skillGeneratingPrompt]);

  const handleCreateSkill = useCallback(async () => {
    if (!skillName.trim() || isCreatingSkill) return;
    setIsCreatingSkill(true);
    setSkillError(null);
    try {
      const createdAmp = await createAmp({
        name: skillName.trim(),
        description: skillDescription.trim() || `Custom Skill: ${skillName.trim()}`,
        category: skillCategory,
        icon: skillIcon,
        color: '#8B5CF6',
        instructions: skillInstructions.trim() || undefined,
        knowledgeBase: skillKnowledgeBaseFile?.name || undefined,
        shareWithCompany: skillShareWithCompany,
      } as any);
      await fetchCustomAmps();
      setSelectedAmps(prev => [...prev, createdAmp.id]);
      resetSkillForm();
      setShowCreateSkill(false);
    } catch (err: any) {
      setSkillError(err.message || 'Failed to create skill. Please try again.');
    } finally {
      setIsCreatingSkill(false);
    }
  }, [skillName, skillDescription, skillCategory, skillIcon, skillInstructions, skillKnowledgeBaseFile, skillShareWithCompany, createAmp, fetchCustomAmps, resetSkillForm, isCreatingSkill]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!name.trim()) {
        setError('Workspace name is required');
        return;
      }

      const channelData: Omit<Channel, 'id' | 'guid' | 'createdAt'> = {
        name: name.trim(),
        description: description.trim(),
        icon: selectedIcon,
        color: selectedColor,
        ampIds: selectedAmps,
        mcpIds: selectedMcps.map(id => {
          const mcp = mcpList.find(m => m.transactionId === id);
          return { name: mcp?.name ?? '', mcpId: id };
        }),
        skillIds: selectedMarketplaceSkills.map(id => {
          const sk = marketplaceSkills.find(s => s.id === id);
          return { name: sk?.name ?? '', skillId: sk?.skillId ?? '' };
        }),
      };

      setIsSubmitting(true);
      setError(null);

      try {
        if (isEditing && onSave && channelToEdit) {
          onSave(channelToEdit.id, channelData);
        } else {
          onAdd(channelData);
        }

        // Reset form only on success
        setName('');
        setDescription('');
        setSelectedIcon('hash');
        setSelectedColor(COLOR_OPTIONS[0]);
        setSelectedAmps([]);
        setAmpSearch('');
        setSelectedCategory('All');
        setSelectedMcps([]);
        setMcpSearch('');
        setSelectedMarketplaceSkills([]);
        setIsWorkspace(false);
        setError(null);
        setShowCreateSkill(false);
        resetSkillForm();
        autoSelectedRef.current = false;
        onClose();
      } catch (err: any) {
        console.error('Failed to save channel:', err);
        setError(err.message || 'Failed to save channel. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, description, selectedIcon, selectedColor, selectedAmps, selectedMcps, mcpList, selectedMarketplaceSkills, marketplaceSkills, isEditing, onSave, onAdd, onClose, channelToEdit, resetSkillForm]
  );

  const resetAndClose = useCallback(() => {
    setName('');
    setDescription('');
    setSelectedIcon('hash');
    setSelectedColor(COLOR_OPTIONS[0]);
    setSelectedAmps([]);
    setAmpSearch('');
    setSelectedCategory('All');
    setSelectedMcps([]);
    setMcpSearch('');
    setIsWorkspace(false);
    setError(null);
    onClose();
  }, [onClose]);

  const formContent = (
    <>
      <div className="add-channel__header">
        <h2 className="add-channel__title">{isEditing ? 'Edit Workspace' : 'Create New Workspace'}</h2>
        <button className="add-channel__close" onClick={resetAndClose}>
          <FiX size={18} />
        </button>
      </div>

      <form className="add-channel__form" onSubmit={handleSubmit}>
        <div className="add-channel__section">
          <div className="add-channel__row">
            <div className="add-channel__field add-channel__field--grow">
              <label className="add-channel__label">Workspace Name *</label>
              <input
                type="text"
                className="glass-input"
                placeholder="e.g., Q2 Renewals"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(null); }}
                required
                autoFocus
                maxLength={50}
              />
            </div>
          </div>

          <div className="add-channel__field">
            <label className="add-channel__label">Description</label>
            <input
              type="text"
              className="glass-input"
              placeholder="What's this channel about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={100}
            />
          </div>

          <button
            type="button"
            className={`add-channel__workspace-toggle ${isWorkspace ? 'add-channel__workspace-toggle--on' : ''}`}
            onClick={() => setIsWorkspace(v => !v)}
          >
            <div className="add-channel__workspace-toggle-text">
              <span className="add-channel__workspace-toggle-label">Make this channel a workspace</span>
              <span className="add-channel__workspace-toggle-desc">Organise multiple Skills and conversations in one place</span>
            </div>
            <div className={`add-channel__switch ${isWorkspace ? 'add-channel__switch--on' : ''}`}>
              <div className="add-channel__switch-thumb" />
            </div>
          </button>

          <div className="add-channel__row add-channel__row--icons">
            <div className="add-channel__field">
              <label className="add-channel__label">Icon</label>
              <div className="add-channel__icons">
                {ICON_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`add-channel__icon-btn ${selectedIcon === opt.id ? 'add-channel__icon-btn--active' : ''}`}
                    onClick={() => setSelectedIcon(opt.id)}
                    title={opt.label}
                    style={selectedIcon === opt.id ? { borderColor: selectedColor, background: `${selectedColor}15` } : undefined}
                  >
                    <span style={selectedIcon === opt.id ? { color: selectedColor } : undefined}>
                      {opt.icon}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="add-channel__field">
              <label className="add-channel__label">Color</label>
              <div className="add-channel__colors">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`add-channel__color-btn ${selectedColor === color ? 'add-channel__color-btn--active' : ''}`}
                    onClick={() => setSelectedColor(color)}
                    style={{ background: color }}
                  >
                    {selectedColor === color && <FiCheck size={12} />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="add-channel__section add-channel__section--amps">
          <div className="add-channel__section-header">
            <div className="add-channel__limit-skills-header">
              <label className="add-channel__label add-channel__label--no-margin">
                Limit Skills
                <span className="add-channel__optional-tag">optional</span>
              </label>
              <span className="add-channel__limit-skills-hint">
                Leave empty to allow all skills · select specific ones to restrict
              </span>
            </div>
            {selectedAmps.length > 0 ? (
              <button
                type="button"
                className="add-channel__clear-btn"
                onClick={() => setSelectedAmps([])}
              >
                Clear ({selectedAmps.length})
              </button>
            ) : (
              <span className="add-channel__all-skills-tag">All skills</span>
            )}
          </div>

          <div className="add-channel__amp-tabs">
            <button
              type="button"
              className={`add-channel__amp-tab ${ampTab === 'premade' ? 'add-channel__amp-tab--active' : ''}`}
              onClick={() => { setAmpTab('premade'); setSelectedCategory('All'); setShowCreateSkill(false); }}
            >
              Momentum Skills
            </button>
            <button
              type="button"
              className={`add-channel__amp-tab ${ampTab === 'yours' ? 'add-channel__amp-tab--active' : ''}`}
              onClick={() => { setAmpTab('yours'); setSelectedCategory('All'); }}
            >
              Your Skills ({combinedYourSkills.length})
            </button>
          </div>

          {ampTab === 'premade' && premadeCategories.length > 1 && (
            <div className="amps-grid-page__filters">
              {premadeCategories.map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`amps-grid-page__filter-pill glass-tag ${selectedCategory === cat ? 'amps-grid-page__filter-pill--active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {ampTab === 'yours' && !showCreateSkill && (
            <button
              type="button"
              className="add-channel__create-skill-btn"
              onClick={() => { setShowCreateSkill(true); resetSkillForm(); }}
            >
              <FiPlus size={13} />
              Create Skill
            </button>
          )}

          {ampTab === 'yours' && showCreateSkill && (
            <motion.div
              className="add-channel__create-skill-form"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="add-channel__create-skill-form-header">
                <motion.button
                  type="button"
                  className="create-amp__back"
                  onClick={() => { setShowCreateSkill(false); resetSkillForm(); }}
                  whileHover={{ x: -2 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiChevronLeft size={14} />
                  Back to Skills
                </motion.button>
                <span className="add-channel__create-skill-title">
                  <FiZap size={13} /> Create New Skill
                </span>
              </div>

              <div>
                <div className="create-amp__fields">

                  {/* Name + Category row */}
                  <div className="create-amp__row">
                    <div className="create-amp__field create-amp__field--grow">
                      <label className="create-amp__label">Name</label>
                      <input
                        className="create-amp__input"
                        type="text"
                        placeholder="e.g. Claims Analyzer"
                        value={skillName}
                        onChange={(e) => setSkillName(e.target.value)}
                        maxLength={40}
                        autoFocus
                        required
                      />
                    </div>
                    <div className="create-amp__field create-amp__field--fixed">
                      <label className="create-amp__label">Category</label>
                      <select
                        className="create-amp__input create-amp__select"
                        value={skillCategory}
                        onChange={(e) => setSkillCategory(e.target.value)}
                      >
                         <option key="default" value="">Select category</option>
                        {skillCategoryOptions.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Icon picker */}
                  <div className="create-amp__field">
                    <label className="create-amp__label">Icon</label>
                    <div className="create-amp__icon-picker">
                      {AVAILABLE_SKILL_ICONS.map(iconName => (
                        <motion.button
                          key={iconName}
                          type="button"
                          className={`create-amp__icon-option ${skillIcon === iconName ? 'create-amp__icon-option--active' : ''}`}
                          onClick={() => setSkillIcon(iconName)}
                          whileHover={{ scale: 1.08 }}
                          whileTap={{ scale: 0.95 }}
                          style={{
                            color: skillIcon === iconName ? '#8B5CF6' : 'var(--text-secondary)',
                            borderColor: skillIcon === iconName ? '#8B5CF660' : 'var(--border-glass)',
                            background: skillIcon === iconName ? '#8B5CF615' : 'var(--surface-input)',
                          }}
                        >
                          {SKILL_ICON_MAP[iconName] || <FiZap size={18} />}
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="create-amp__field">
                    <label className="create-amp__label">Description</label>
                    <textarea
                      className="create-amp__input create-amp__textarea"
                      placeholder="What does this Skill do?"
                      value={skillDescription}
                      onChange={(e) => setSkillDescription(e.target.value)}
                      maxLength={200}
                      rows={3}
                    />
                  </div>

                  {/* Instructions with AI generate */}
                  <div className="create-amp__field">
                    <label className="create-amp__label">
                      Instructions <span className="create-amp__hint">optional</span>
                    </label>
                    <div className="create-amp__textarea-wrapper">
                      <textarea
                        className="create-amp__input create-amp__textarea"
                        placeholder="Skills or behavior instructions for the AI..."
                        value={skillInstructions}
                        onChange={(e) => setSkillInstructions(e.target.value)}
                        rows={5}
                      />
                      <motion.button
                        type="button"
                        className={`create-amp__ai-btn ${skillGeneratingPrompt ? 'create-amp__ai-btn--loading' : ''}`}
                        onClick={handleGenerateSkillPrompt}
                        disabled={skillGeneratingPrompt}
                        whileHover={{ scale: !skillGeneratingPrompt ? 1.08 : 1 }}
                        whileTap={{ scale: !skillGeneratingPrompt ? 0.95 : 1 }}
                        title={skillGeneratingPrompt ? 'Generating...' : 'Generate AI prompt'}
                      >
                        {skillGeneratingPrompt ? (
                          <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
                            <RiSparklingFill size={16} />
                          </motion.div>
                        ) : (
                          <RiSparklingFill size={16} />
                        )}
                      </motion.button>
                    </div>
                  </div>

                  {/* Knowledge Base */}
                  <div className="create-amp__field">
                    <label className="create-amp__label">
                      Knowledge Base <span className="create-amp__hint">optional</span>
                    </label>
                    <div
                      className={`create-amp__file-upload ${skillDragActive ? 'create-amp__file-upload--active' : ''} ${skillKnowledgeBaseFile ? 'create-amp__file-upload--filled' : ''}`}
                      onDragEnter={handleSkillDrag}
                      onDragLeave={handleSkillDrag}
                      onDragOver={handleSkillDrag}
                      onDrop={handleSkillDrop}
                    >
                      <input
                        type="file"
                        id="skill-kb-file"
                        className="create-amp__file-input create-amp__file-input--hidden"
                        onChange={handleSkillFileSelect}
                        accept=".txt,.pdf,.doc,.docx"
                      />
                      {skillKnowledgeBaseFile ? (
                        <motion.div className="create-amp__file-display" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                          <div className="create-amp__file-icon"><FiFileText size={18} /></div>
                          <div className="create-amp__file-info">
                            <p className="create-amp__file-name">{skillKnowledgeBaseFile.name}</p>
                            <p className="create-amp__file-size">{(skillKnowledgeBaseFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <motion.button type="button" className="create-amp__file-remove" onClick={() => setSkillKnowledgeBaseFile(null)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <FiX size={16} />
                          </motion.button>
                        </motion.div>
                      ) : (
                        <motion.div className="create-amp__file-placeholder" animate={{ scale: skillDragActive ? 1.02 : 1 }}>
                          <div className="create-amp__file-icon-empty"><FiFileText size={24} /></div>
                          <p className="create-amp__file-label-main">Drag and drop your knowledge base</p>
                          <p className="create-amp__file-label-sub">
                            or{' '}
                            <button type="button" className="create-amp__file-browse" onClick={() => document.getElementById('skill-kb-file')?.click()}>
                              browse files
                            </button>
                          </p>
                          <p className="create-amp__file-hint">PDF, TXT, DOC, DOCX (Max 10 MB)</p>
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Share with Company */}
                  <div className="create-amp__field">
                    <label className="create-amp__label create-amp__checkbox-label">
                      <input
                        type="checkbox"
                        checked={skillShareWithCompany}
                        onChange={(e) => setSkillShareWithCompany(e.target.checked)}
                      />
                      Share with Company
                    </label>
                  </div>

                  {skillError && (
                    <div className="add-channel__error">
                      <FiAlertTriangle size={13} />
                      {skillError}
                    </div>
                  )}

                  <div className="create-amp__actions">
                    <motion.button
                      className="glass-btn glass-btn-primary"
                      type="button"
                      disabled={!skillName.trim() || isCreatingSkill}
                      onClick={handleCreateSkill}
                      whileHover={{ scale: !isCreatingSkill ? 1.02 : 1 }}
                      whileTap={{ scale: !isCreatingSkill ? 0.97 : 1 }}
                    >
                      <FiPlus size={15} />
                      {isCreatingSkill ? 'Creating Skill...' : 'Create Skill'}
                    </motion.button>
                  </div>

                </div>
              </div>
            </motion.div>
          )}

          {!showCreateSkill && (
            <>
              <div className="add-channel__amp-search">
                <FiSearch size={14} className="add-channel__amp-search-icon" />
                <input
                  type="text"
                  className="add-channel__amp-search-input"
                  placeholder="Search Skills..."
                  value={ampSearch}
                  onChange={(e) => setAmpSearch(e.target.value)}
                />
              </div>

              <div className="add-channel__amp-grid">
                {ampsLoading || (ampTab === 'yours' && marketplaceSkillsLoading) ? (
                  <>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={`skill-skeleton-${i}`} className="add-channel__amp-card add-channel__amp-card--skeleton" aria-hidden="true">
                        <div className="add-channel__amp-icon add-channel__amp-icon--skeleton" />
                        <div className="add-channel__amp-info">
                          <div className="add-channel__amp-line add-channel__amp-line--name" />
                          <div className="add-channel__amp-line add-channel__amp-line--category" />
                        </div>
                      </div>
                    ))}
                  </>
                ) : ampsError ? (
                  <div className="add-channel__amp-empty add-channel__amp-empty--error">
                    <FiAlertTriangle size={20} />
                    <span>Failed to load Skills. Please try again.</span>
                  </div>
                ) : filteredAmps.length === 0 ? (
                  <div className="add-channel__amp-empty">
                    {ampTab === 'yours' ? <>No Skills yet — create one or install one from the Marketplace.</> : <>No Skills match your search.</>}
                  </div>
                ) : (
                  filteredAmps.map((amp) => {
                    const isMarketplace = amp.__source === 'marketplace';
                    const isSelected = isMarketplace ? selectedMarketplaceSkills.includes(amp.id) : selectedAmps.includes(amp.id);
                    const onToggle = () => isMarketplace ? toggleMarketplaceSkill(amp.id) : toggleAmp(amp.id);
                    return (
                      <motion.button
                        key={amp.id}
                        type="button"
                        className={`add-channel__amp-card ${isSelected ? 'add-channel__amp-card--selected' : ''}`}
                        onClick={onToggle}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div
                          className="add-channel__amp-icon"
                          style={{ background: `${amp.color}15`, color: amp.color }}
                        >
                          {isMarketplace
                            ? (amp.icon ? <span style={{ fontSize: 16 }}>{amp.icon}</span> : <FiZap size={16} />)
                            : (AMP_ICON_MAP[amp.icon] || <FiZap size={16} />)}
                        </div>
                        <div className="add-channel__amp-info">
                          <div className="add-channel__amp-name">{amp.name}</div>
                          <div className="add-channel__amp-category">{amp.category}</div>
                        </div>
                        {isSelected && (
                          <div className="add-channel__amp-check" style={{ background: amp.color }}>
                            <FiCheck size={12} />
                          </div>
                        )}
                      </motion.button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* MCP Servers Section — every MCP the user has access to, whether added directly
            or provisioned by installing a Marketplace MCP tool (both land in the same
            ExternalMCP-backed list server-side) */}
        <div className="add-channel__section add-channel__section--amps">
          <div className="add-channel__section-header">
            <label className="add-channel__label">Select MCP Servers</label>
            {selectedMcps.length > 0 && (
              <span className="add-channel__amp-count">{selectedMcps.length} selected</span>
            )}
          </div>

          <div className="add-channel__amp-search">
            <FiSearch size={14} className="add-channel__amp-search-icon" />
            <input
              type="text"
              className="add-channel__amp-search-input"
              placeholder="Search MCP servers..."
              value={mcpSearch}
              onChange={e => setMcpSearch(e.target.value)}
            />
          </div>

          <div className="add-channel__amp-grid">
            {mcpLoading ? (
              <div className="add-channel__amp-empty">Loading MCP servers...</div>
            ) : filteredMcps.length === 0 ? (
              <div className="add-channel__amp-empty">
                {mcpList.length === 0 ? 'No MCP servers added yet.' : 'No servers match your search.'}
              </div>
            ) : (
              filteredMcps.map(mcp => (
                <motion.button
                  key={mcp.transactionId}
                  type="button"
                  className={`add-channel__amp-card ${selectedMcps.includes(mcp.transactionId) ? 'add-channel__amp-card--selected' : ''}`}
                  onClick={() => toggleMcp(mcp.transactionId)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="add-channel__amp-icon add-channel__amp-icon--mcp-default">
                    <FiZap size={16} />
                  </div>
                  <div className="add-channel__amp-info">
                    <div className="add-channel__amp-name">{mcp.name}</div>
                    <div className="add-channel__amp-category add-channel__amp-category--ellipsis">{mcp.url}</div>
                  </div>
                  {selectedMcps.includes(mcp.transactionId) && (
                    <div className="add-channel__amp-check add-channel__amp-check--mcp-default">
                      <FiCheck size={12} />
                    </div>
                  )}
                </motion.button>
              ))
            )}
          </div>
        </div>

        {error && (
          <motion.div
            className="add-channel__error"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FiAlertTriangle size={14} />
            {error}
          </motion.div>
        )}
      </form>

      <div className="add-channel__footer">
        <motion.button
          type="button"
          className="glass-btn glass-btn-primary add-channel__submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={!name.trim() || isSubmitting || ampsLoading}
          onClick={handleSubmit}
        >
          {isSubmitting ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Workspace')}
        </motion.button>
      </div>
    </>
  );

  if (inline) {
    return isOpen ? <div className="add-channel__inline">{formContent}</div> : null;
  }

  return (
    <SafeAnimatePresence>
      {isOpen && (
        <motion.div
          className="add-channel__backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={resetAndClose}
        >
          <motion.div
            className="add-channel__modal add-channel__modal--large glass-elevated"
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 24 }}
            transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {formContent}
          </motion.div>
        </motion.div>
      )}
    </SafeAnimatePresence>
  );
};

export default AddChannelModal;
